import identity from 'lodash/identity';
import { useQueryClient } from 'react-query';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { getFetcher } from 'src/core/api/fetcher';
import { useQuery } from 'src/core/api/hooks/useQuery';
import { type QueryError } from 'src/core/api/queryError';
import { type QueryState } from 'src/core/api/queryState';

import type { PurchaseOrderDetails, Appraisal, ApprovalState } from '../models';

type PurchaseOrderDetailsRequestError = {
  errorCode: string;
};

type Options = {
  enabled?: boolean;
  onSuccess?(purchaseOrder: PurchaseOrderDetails): void;
  onError?(error: QueryError<unknown>): void;
};

export type RawPurchaseOrderDetails = PurchaseOrderDetails & {
  // FIXME: needed for backward compatibility, to remove after the rollout
  // of the related backend changes
  appraisals: Appraisal[];
  approval_state: ApprovalState;
};

export const usePurchaseOrderQuery = (
  purchaseOrderId?: string,
  { enabled = true, onSuccess, onError }: Options = {},
): QueryState<PurchaseOrderDetails, PurchaseOrderDetailsRequestError> => {
  return useQuery<
    PurchaseOrderDetails,
    RawPurchaseOrderDetails,
    PurchaseOrderDetailsRequestError
  >({
    key: ['purchase-orders', purchaseOrderId],
    isEnabled: Boolean(purchaseOrderId) && enabled,
    request: {
      type: 'rest',
      target: 'companyAPI',
      endpoint: `/purchase-orders/${purchaseOrderId}`,
    },
    options: {
      onSuccess: ({ data }) => {
        if (onSuccess) {
          onSuccess(data);
        }
      },
      onError: ({ error }) => {
        if (onError) {
          onError(error);
        }
      },
    },
    reshapeData: identity,
  });
};

export const usePurchaseOrderQueryPromise = () => {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();

  return async (purchaseOrderId: string): Promise<PurchaseOrderDetails> => {
    return queryClient.fetchQuery(
      ['purchaseOrders', purchaseOrderId],
      getFetcher({
        companyId,
        getRequest: () => ({
          type: 'rest',
          target: 'companyAPI',
          endpoint: `/purchase-orders/${purchaseOrderId}`,
          method: 'get',
        }),
      }),
    );
  };
};

export const useInvalidatePurchaseOrderQuery = () => {
  const queryClient = useQueryClient();

  return async (purchaseOrderId: string): Promise<void> => {
    await queryClient.invalidateQueries(['purchase-orders', purchaseOrderId]);
  };
};
